import React from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

function PrivacyPolicy() {
  return (
    <Stack direction="row" justifyContent="center" sx={{ width: "100%" }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: "1100px",
          padding: { xs: 2, md: 5 },
        }}
      >
        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last Updated: August 12, 2026
        </Typography>

        <Typography variant="h5" gutterBottom>
          1. Introduction
        </Typography>
        <Typography paragraph>
          This Privacy Policy explains how our Human Resource Management System
          (HRMS) collects, uses, stores, protects, and processes personal and
          employment-related information. We are committed to protecting the
          privacy and security of all employees, administrators, and authorized
          users of the HRMS.
        </Typography>

        <Typography variant="h5" gutterBottom>
          2. Information We Collect
        </Typography>
        <Typography paragraph>
          Depending on the features used, the HRMS may collect and process the
          following information:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <Typography>Employee name and employee ID</Typography>
          </li>
          <li>
            <Typography>Email address and contact number</Typography>
          </li>
          <li>
            <Typography>Department, designation, and employment details</Typography>
          </li>
          <li>
            <Typography>Login credentials and authentication information</Typography>
          </li>
          <li>
            <Typography>Attendance, clock-in, and clock-out information</Typography>
          </li>
          <li>
            <Typography>Working hours, shifts, late arrival, and overtime records</Typography>
          </li>
          <li>
            <Typography>Leave applications and leave balances</Typography>
          </li>
          <li>
            <Typography>Payroll and compensation-related information</Typography>
          </li>
          <li>
            <Typography>Documents uploaded by authorized users</Typography>
          </li>
          <li>
            <Typography>System activity, browser, device, and technical information</Typography>
          </li>
        </Box>

        <Typography variant="h5" gutterBottom>
          3. How We Use Information
        </Typography>
        <Typography paragraph>
          Information collected through the HRMS is used only for legitimate
          business and human-resource management purposes, including:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <Typography>Managing employee records</Typography>
          </li>
          <li>
            <Typography>Tracking attendance and working hours</Typography>
          </li>
          <li>
            <Typography>Managing shifts, overtime, and holidays</Typography>
          </li>
          <li>
            <Typography>Processing leave requests</Typography>
          </li>
          <li>
            <Typography>Supporting payroll and compensation administration</Typography>
          </li>
          <li>
            <Typography>Managing user accounts and access permissions</Typography>
          </li>
          <li>
            <Typography>Maintaining system security and preventing unauthorized access</Typography>
          </li>
          <li>
            <Typography>Improving the performance and functionality of the HRMS</Typography>
          </li>
        </Box>

        <Typography variant="h5" gutterBottom>
          4. Attendance and Location Information
        </Typography>
        <Typography paragraph>
          If the HRMS includes attendance or location-based features, relevant
          information may be collected to verify employee attendance, working
          hours, or authorized work locations. Such information will only be
          used for legitimate attendance and workforce-management purposes and
          will be accessible only to authorized personnel.
        </Typography>

        <Typography variant="h5" gutterBottom>
          5. Data Sharing
        </Typography>
        <Typography paragraph>
          We do not sell, rent, or trade employee personal information. Data may
          be shared with authorized administrators, HR personnel, management,
          payroll personnel, or trusted service providers where necessary to
          operate the HRMS and provide related services.
        </Typography>

        <Typography variant="h5" gutterBottom>
          6. Data Security
        </Typography>
        <Typography paragraph>
          We take reasonable technical and organizational measures to protect
          personal information against unauthorized access, alteration,
          disclosure, loss, or destruction. Access to HRMS information is
          controlled through authentication and role-based permissions where
          applicable.
        </Typography>

        <Typography variant="h5" gutterBottom>
          7. Data Retention
        </Typography>
        <Typography paragraph>
          Personal and employment-related information will be retained for as
          long as reasonably necessary to fulfill HR, payroll, legal,
          regulatory, contractual, and operational requirements. When
          information is no longer required, it may be securely deleted or
          anonymized in accordance with applicable policies and laws.
        </Typography>

        <Typography variant="h5" gutterBottom>
          8. User Responsibilities
        </Typography>
        <Typography paragraph>
          Users are responsible for keeping their login credentials confidential
          and must not share their account credentials with unauthorized persons.
          Users should immediately report suspected unauthorized access or
          security incidents to the HRMS administrator.
        </Typography>

        <Typography variant="h5" gutterBottom>
          9. Cookies and Technical Information
        </Typography>
        <Typography paragraph>
          The HRMS may use cookies, local storage, session information, and
          similar technologies to maintain authentication sessions, remember
          user preferences, improve security, and provide system functionality.
        </Typography>

        <Typography variant="h5" gutterBottom>
          10. Third-Party Services
        </Typography>
        <Typography paragraph>
          The HRMS may use trusted third-party services for hosting, email,
          authentication, notifications, analytics, payment processing, or
          other operational requirements. Such services may process information
          only as necessary to provide their respective services and subject to
          applicable privacy and security requirements.
        </Typography>

        <Typography variant="h5" gutterBottom>
          11. Employee Privacy Rights
        </Typography>
        <Typography paragraph>
          Subject to applicable laws and company policies, employees may have
          rights to access, correct, update, or request deletion of certain
          personal information maintained in the HRMS. Requests should be
          submitted to the organization's HR or system administrator.
        </Typography>

        <Typography variant="h5" gutterBottom>
          12. Children's Privacy
        </Typography>
        <Typography paragraph>
          The HRMS is intended for employees, administrators, and other
          authorized users and is not designed for use by children.
        </Typography>

        <Typography variant="h5" gutterBottom>
          13. Changes to This Privacy Policy
        </Typography>
        <Typography paragraph>
          We may update this Privacy Policy from time to time to reflect changes
          in our HRMS, business practices, security requirements, or applicable
          laws. Any updated policy will be published through the HRMS or the
          organization's designated communication channels.
        </Typography>

        <Typography variant="h5" gutterBottom>
          14. Contact Us
        </Typography>
        <Typography paragraph>
          If you have questions, concerns, or requests regarding this Privacy
          Policy or the handling of your personal information, please contact
          your HR department or the HRMS administrator.
        </Typography>
      </Box>
    </Stack>
  );
}

export default PrivacyPolicy;